'use client';

import { useState, useEffect } from 'react';
import { useRouter, usePathname } from 'next/navigation';
import Link from 'next/link';
import { AdminSidebar } from './AdminSidebar';
import NotificationBell from './NotificationBell';
import { LogOut, User, Settings } from 'lucide-react';

interface AdminLayoutProps {
  children: React.ReactNode;
}

export function AdminLayout({ children }: AdminLayoutProps) {
  const router = useRouter();
  const pathname = usePathname();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [siteName, setSiteName] = useState('');

  // 登录页不显示布局
  const isLoginPage = pathname?.includes('/login');

  // 获取用户信息和站点配置
  useEffect(() => {
    if (isLoginPage) return;

    const abortController = new AbortController();

    const userStr = sessionStorage.getItem('adminUser');
    if (userStr) {
      setCurrentUser(JSON.parse(userStr));
    }

    loadSiteConfig(abortController.signal);

    return () => {
      abortController.abort();
    };
  }, [isLoginPage]);

  const loadSiteConfig = async (signal?: AbortSignal) => {
    try {
      const response = await fetch('/api/admin/site-config?public=true', {
        signal
      });
      const result = await response.json();
      if (result.success) {
        setSiteName(result.siteName || '');
      }
    } catch (error) {
      // 忽略因组件卸载导致的中断错误
      if (error instanceof Error && error.name === 'AbortError') {
        return;
      }
      console.error('加载站点配置失败:', error);
    }
  };

  const handleLogout = async () => {
    try {
      // 🔒 安全：调用服务器API销毁session
      await fetch('/api/admin/logout', {
        method: 'POST',
        credentials: 'include'
      });

      // 清除所有sessionStorage
      sessionStorage.removeItem('adminLoggedIn');
      sessionStorage.removeItem('adminLoginTime');
      sessionStorage.removeItem('adminUser');
      sessionStorage.removeItem('adminToken');
      sessionStorage.clear(); // 完全清空

      // 清除JWT cookie（多种方式确保清除）
      document.cookie = 'admin-token=; Path=/; Expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Strict';
      document.cookie = 'admin-token=; Path=/; Max-Age=0';

      // 跳转到登录页
      router.push('/admin-2025/login');

      // 强制刷新页面，确保所有状态清除
      setTimeout(() => {
        window.location.href = '/admin-2025/login';
      }, 100);
    } catch (error) {
      console.error('退出登录失败:', error);
      // 即使出错也要清除本地状态并跳转
      sessionStorage.clear();
      document.cookie = 'admin-token=; Path=/; Expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Strict';
      window.location.href = '/admin-2025/login';
    }
  };

  // 登录页直接返回children
  if (isLoginPage) {
    return <>{children}</>;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* 侧边栏 */}
      <AdminSidebar
        collapsed={sidebarCollapsed}
        onCollapsedChange={setSidebarCollapsed}
      />

      {/* 主内容区 */}
      <div
        className={`
          transition-all duration-300
          ${sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-64'}
        `}
      >
        {/* 顶栏 - 手机端不固定，可以滚动消失 */}
        <header className="sm:sticky sm:top-0 z-20 bg-white border-b border-gray-200 h-12 sm:h-16">
          <div className="flex items-center justify-between h-full px-3 sm:px-4 lg:px-6">
            {/* 左侧：站点名称 */}
            <div className="flex items-center gap-4">
              <h1 className="text-lg font-semibold text-gray-900 hidden sm:block">
                {siteName || '管理后台'}
              </h1>
            </div>

            {/* 右侧：用户信息和操作 */}
            <div className="flex items-center gap-2 sm:gap-4">
              {/* 通知铃铛 */}
              <NotificationBell />

              {/* 用户下拉菜单 */}
              {currentUser && (
                <div className="flex items-center gap-2 sm:gap-3">
                  <div className="hidden sm:block text-right">
                    <div className="text-sm font-medium text-gray-900">
                      {currentUser.username}
                    </div>
                    <div className="text-xs text-gray-500">
                      {currentUser.role === 'super_admin' ? '超级管理员' : '管理员'}
                    </div>
                  </div>

                  <Link
                    href="/admin-2025/profile"
                    className="w-7 h-7 sm:w-9 sm:h-9 rounded-full bg-blue-100 flex items-center justify-center hover:bg-blue-200 transition-colors"
                    title="个人设置"
                  >
                    <User className="w-4 h-4 sm:w-[18px] sm:h-[18px] text-blue-600" />
                  </Link>

                  <button
                    onClick={handleLogout}
                    className="p-1.5 sm:p-2 rounded-lg hover:bg-red-50 text-gray-500 hover:text-red-600 transition-colors"
                    title="退出登录"
                  >
                    <LogOut className="w-4 h-4 sm:w-5 sm:h-5" />
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* 页面内容 */}
        <main className="p-2 sm:p-4 lg:p-6">
          {children}
        </main>
      </div>
    </div>
  );
}
